<template>

  <shell :title="$t('titles.products')">
  
    <table-tools :actions="tableActions" :headers="headers" v-model:enabled-headers="enabledHeaders" remember-headers="product-index">

      <table-filter v-if="canManageOrganizations">
        <o-autocomplete
          :label="$t('titles.organization')"
          v-model.number="filters.organizationID"
          :items="Organization.getOptions()"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-text-field
          :label="$t('titles.name')"
          v-model="filters.name"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-select
          :label="$t('titles.vertical')"
          v-model="filters.verticalType"
          :items="VerticalRegistry.getOptions()"
          clearable
        />
      </table-filter>

    </table-tools>

    <v-data-table
      ref="dataTable"
      :items="products"
      :headers="visibleHeaders"
      :sort-by="sortBy"
      v-model:page="page"
      :items-per-page="itemsPerPage"
      :loading="loadingProducts"
    >

      <template v-slot:item.productType="{ item }">        
        {{ $t(`enums.product_types.${item.productType}`) }}
      </template>

      <template v-slot:item.verticalType="{ item }">
        {{ $t(`enums.verticals.${item.verticalType}`) }}
      </template>

      <template v-slot:item.actions="{ item }">

        <ActionGroup
          :actions="actions"
          @edit-clicked="editProduct(item)"
          @delete-clicked="deleteProduct(item)"
        />

      </template>

      <template v-slot:bottom>
        <data-table-pagination
          :items="products"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          @update:page="dataTable?.$el.scrollIntoView()"
        />
      </template>

    </v-data-table>

  </shell>
  
</template>

<script setup lang="ts">

import { ref, computed, reactive, inject, watch, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import Shell from '../Shell.vue'
import DataTablePagination from '../../DataTablePagination.vue'
import store from '../../../plugins/vuex'
import ProductModal from './ProductModal.vue'
import ProductDeleteModal from './ProductDeleteModal.vue'

// models
import Organization from '../../../models/Organization'
import Product from '../../../models/Product'

// misc
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import NotificationService from '../../../services/NotificationService'
import { QuerySyncer, NumberSerializer } from '../../../utils/QuerySyncer'
import VerticalRegistry from '../../../verticals/VerticalRegistry'
import type { Can } from '../../../main'
import type { ActionGroupAction, TableToolsAction } from '../../../types/misc'

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.products')

const $can = inject('$can') as Can
const canManageOrganizations = $can('organizations.manage')
const canEditProducts = $can('products.write')

const actions: ActionGroupAction[] = []

if (canEditProducts) {
  actions.push({ label: t('actions.edit'), icon: '$edit', event: 'editClicked' })
  actions.push({ label: t('actions.delete'), icon: '$delete', event: 'deleteClicked' })
} else {
  actions.push({ label: t('actions.view'), icon: '$view', event: 'editClicked' })
}

const filters = reactive({
  organizationID: null,
  name: '',
  verticalType: null,
})

new QuerySyncer({
  organization_id: {
    watchable: () => filters.organizationID,
    hydrate: (newValue) => filters.organizationID = newValue,
    serializer: NumberSerializer,
  },
  name: {
    watchable: () => filters.name,
    hydrate: (newValue) => filters.name = newValue,
  },
  vertical: {
    watchable: () => filters.verticalType,
    hydrate: (newValue) => filters.verticalType = newValue,
  },
})

const tableActions = computed<TableToolsAction[]>(() => [
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadProducts,
    busy: loadingProducts.value,
  },
  {
    title: t('actions.create_product'),
    icon: '$create',
    click: createProduct,
    hidden: !canEditProducts,
  },
])

/**
 * Data table configs
 */

const dataTable = useTemplateRef('dataTable')

const sortBy = ref([{ key: 'name' }])

const headers = [
  {title: t('titles.id'), key: 'ID', width: 100, hidden: !$can('seeids')},
  {title: t('titles.organization'), key: 'organization.name', width: 300, hidden: !canManageOrganizations},
  {title: t('titles.product'), key: 'name', toggleable: false},
  {title: t('titles.product_type'), key: 'productType', width: 160},
  {title: t('titles.vertical'), key: 'verticalType', width: 160},
  {title: '', key: 'actions', sortable: false, width: 120, hideFromPicker: true},
]

const enabledHeaders = ref([
  'ID', 'organization.name', 'name', 'productType', 'verticalType', 'actions',
])

const visibleHeaders = computed(() => {
  return headers.filter((header) => {
    return !header.hidden && enabledHeaders.value.includes(header.key)
  })
})

/**
 * Pagination
 */

const page = ref<number>(1)
const itemsPerPage = ref<number>(10)

// when anything that affects the main query is changed, reset the page to 1
watch(filters, () => page.value = 1)

/**
 * Product data
 */

const loadingProducts = ref<boolean>(false)

const products = computed<Product[]>(() => {
  
  const query = Product.query()
    .with('organization')
    .orderBy(product => {
      return (product as Product).name.toUpperCase()
    })

  if (filters.verticalType) {
    query.where('verticalType', filters.verticalType)
  }

  if (filters.organizationID !== null) {
    query.where('organizationID', filters.organizationID)
  }

  if (filters.name !== null && filters.name.length > 0) {
    query.where((product: Product) => {
      return product.name.toUpperCase().indexOf(
        filters.name.toUpperCase()
      ) !== -1
    })
  }

  return query.get()

})

function loadProducts() {

  loadingProducts.value = true

  Product.fetchAll()
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_products'))
    })
    .finally(() => {
      loadingProducts.value = false
    })

}

/**
 * Modals
 */

function createProduct() {
  openDialog(ProductModal, {
    product: new Product({
      organizationID: store.state.auth.user.organizationID,
    }),
  })
}

function editProduct(product: Product) {
  openDialog(ProductModal, { product })
}

function deleteProduct(product: Product) {
  openDialog(ProductDeleteModal, { product })
}

</script>
